import { useEffect } from 'react';
import { get } from './api';

/**
 * The front desk's emergency list, kept in this browser so it is readable when the internet or the server
 * is down. Refreshed every five minutes while a front-desk user is signed in; removed at sign-out and after
 * 24 hours.
 */
export interface EmergencyStay {
  confirmationNo: string;
  guestName: string;
  room: string | null;
  roomType: string;
  arrivalDate: string;
  departureDate: string;
  adults: number;
  children: number;
  vipCode: string | null;
  eta: string | null;
  company: string | null;
  balance: string;
}
export interface EmergencyList {
  generatedAt: string;
  businessDate: string;
  hotel: string;
  phone: string | null;
  inHouse: EmergencyStay[];
  arrivals: EmergencyStay[];
  departures: EmergencyStay[];
  rooms: { number: string; floor: number; roomType: string; hkStatus: string; occupancy: string }[];
  totals: { rooms: number; adults: number; children: number };
}

const KEY = 'pms.emergency.v1';
const MAX_AGE_MS = 24 * 3_600_000;

export function saveEmergency(data: EmergencyList) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {
    // Storage blocked or full: the list simply is not kept on this computer.
  }
}

export function loadEmergency(): { savedAt: number; data: EmergencyList } | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { savedAt: number; data: EmergencyList };
    if (Date.now() - v.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

export function clearEmergency() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}

export async function refreshEmergency(): Promise<boolean> {
  try {
    saveEmergency(await get<EmergencyList>('/frontdesk/emergency-list'));
    return true;
  } catch {
    return false;
  }
}

export function useEmergencySync(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    void refreshEmergency();
    const t = setInterval(() => void refreshEmergency(), 5 * 60_000);
    return () => clearInterval(t);
  }, [enabled]);
}
